import React, { useEffect, useRef, useState } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import { createPortal } from "react-dom";
import {
  wrapper,
  hidden,
  bubblePortal,
  bubblePortalInverted,
  tooltipTop,
  tooltipLeft,
  arrowOffset,
} from "./styles_vanilla.css";

type ExtendedInfoTooltipProps = {
  message: string;
  inverted?: boolean;
  children: React.ReactNode;
  /** Extra class(es) applied to the tooltip bubble, e.g. `preserveLineBreaks` for multi-line messages. */
  className?: string;
};

export const ExtendedInfoTooltip: React.FC<ExtendedInfoTooltipProps> = ({
  message,
  inverted = false,
  children,
  className,
}) => {
  const [hover, setHover] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    arrowOffset: number;
  } | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hover || !message) return;

    const wrapperEl = ref.current;
    if (!wrapperEl) return;
    const rect = wrapperEl.getBoundingClientRect();

    const tooltipWidth = 300;
    const viewportWidth = window.innerWidth;
    const triggerCenter = rect.left + rect.width / 2;
    const preferredLeft = triggerCenter - tooltipWidth / 2;

    let adjustedLeft = preferredLeft;
    if (preferredLeft + tooltipWidth > viewportWidth - 10) {
      adjustedLeft = viewportWidth - tooltipWidth - 10;
    }

    if (adjustedLeft < 10) {
      adjustedLeft = 10;
    }

    const computedArrowOffset = triggerCenter - adjustedLeft;
    const anchorTop = inverted ? rect.bottom : rect.top;

    setCoords({ top: anchorTop, left: adjustedLeft, arrowOffset: computedArrowOffset });
  }, [hover, message, inverted]);

  if (!message) {
    return <>{children}</>;
  }

  return (
    <div
      ref={ref}
      className={wrapper}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
      {hover && coords
        ? createPortal(
            <div
              className={classNames(inverted ? bubblePortalInverted : bubblePortal, className)}
              style={assignInlineVars({
                [tooltipTop]: `${coords.top}px`,
                [tooltipLeft]: `${coords.left}px`,
                [arrowOffset]: `${coords.arrowOffset}px`,
              })}
              role="tooltip"
            >
              {message}
            </div>,
            document.body,
          )
        : null}
      <div aria-hidden="true" className={hidden}>
        {message}
      </div>
    </div>
  );
};
